// 기간 프리셋/비교 기간 계산 공통 유틸 (SSOT)
// DashboardView, IntegratedSalesView 등 기간 필터를 쓰는 화면과 통계 네 화면(축 기본 기간)이 공유한다.
// label 은 i18n 키(한글)이므로 컴포넌트에서 $t(label) 로 출력한다.

// 확장자를 적는 이유: 단위 테스트가 이 파일을 node 에서 그대로 읽는다(node ESM 은 확장자를 추론하지 않는다)
import {
  addCalendarDays,
  addCalendarMonths,
  calendarDaysBetween,
  formatKst,
  kstTodayLocal,
  shiftKst,
} from './kstDate.js'

export const PERIOD_PRESETS = [
  { type: 'TODAY', label: '오늘', icon: 'el-icon-sunny' },
  { type: 'YESTERDAY', label: '어제', icon: 'el-icon-time' },
  { type: 'THIS_WEEK', label: '이번주', icon: 'el-icon-date' },
  { type: 'THIS_MONTH', label: '이번달', icon: 'el-icon-s-data' },
  { type: 'LAST_MONTH', label: '지난달', icon: 'el-icon-d-arrow-left' },
]

// 기간 비교 타입 (전년/전월/이전기간/직접)
export const COMPARE_TYPES = [
  { value: 'PREV_YEAR', label: '전년 동기' },
  { value: 'PREV_MONTH', label: '전월 대비' },
  { value: 'PREV_PERIOD', label: '이전 기간' },
  { value: 'CUSTOM', label: '직접 선택' },
]

export function formatDateStr(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 프리셋 타입 → { startDate, endDate } (yyyy-MM-dd)
// 「오늘」은 KST 달력이다(kstDate) — 보는 사람의 시간대가 끼어들면 KST 가 아닌 브라우저에서 프리셋이 하루 어긋난다.
// `nowMs` 는 시험이 시계를 줄 때만 넘긴다.
export function getDateRangeForPeriod(periodType, nowMs = Date.now()) {
  const today = kstTodayLocal(nowMs)
  let startDate
  let endDate

  switch (periodType) {
    case 'TODAY':
      startDate = new Date(today)
      endDate = new Date(today)
      break
    case 'YESTERDAY':
      startDate = new Date(today)
      startDate.setDate(startDate.getDate() - 1)
      endDate = new Date(startDate)
      break
    case 'THIS_WEEK': {
      // 이번 주 월요일부터 오늘까지
      startDate = new Date(today)
      const dayOfWeek = startDate.getDay()
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      startDate.setDate(startDate.getDate() - diff)
      endDate = new Date(today)
      break
    }
    case 'THIS_MONTH':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1)
      endDate = new Date(today)
      break
    case 'LAST_MONTH':
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      endDate = new Date(today.getFullYear(), today.getMonth(), 0)
      break
    default:
      startDate = new Date(today)
      endDate = new Date(today)
  }

  return { startDate: formatDateStr(startDate), endDate: formatDateStr(endDate) }
}

/**
 * **통계 화면에서 축(시간별·일별·월별·년별)을 고르면 세우는 기본 기간** — KST 달력. 순수하다(지금은 부르는 화면이 준다).
 *
 * 통계 네 화면(매출·상품별·시간대별·사업장)이 같은 감시자를 통째로 베껴 셈하던 자리다. 그 사본의 월별이
 * `dateAdd('M ', -3)` 였는데 간격 어휘에서 `'M '` 은 **분**이라(`kstDate.shiftKst`) 늘 이번 달 한 칸만 조회했다(R-O X4).
 * 월별은 다른 축과 같은 모양이다 — 석 달 전 달부터 이번 달까지(년별이 3년 전 해부터 올해까지이듯). 달 셈은 말일을
 * 넘치지 않는 [addCalendarMonths] 다(5/31 의 석 달 전은 2월이지 3월이 아니다). 시간별·일별·년별은 종전 값 그대로다.
 *
 * @param {'HOUR'|'DAY'|'MONTH'|'YEAR'} axis 고른 축(검색 폼의 `dateSearchTpVal`)
 * @param {number} nowMs 지금(epoch ms)
 * @returns {{seDt: Array<string>}|{startDt: string, endDt: string}|{}} 검색 폼에 그대로 얹을 칸 — 시간별·일별·월별은
 *   `seDt`, 년별은 `startDt`·`endDt`. 모르는 축이면 빈 객체(아무 칸도 바꾸지 않는다)
 */
export function statisticsAxisRange(axis, nowMs) {
  const today = formatKst('yyyy-MM-dd', nowMs)

  switch (axis) {
    case 'HOUR':
      return { seDt: [formatKst('yyyy-MM-dd HH:mm', shiftKst('h ', -2, nowMs)), formatKst('yyyy-MM-dd HH:mm', nowMs)] }
    case 'DAY':
      return { seDt: [addCalendarDays(today, -7), today] }
    case 'MONTH':
      return { seDt: [addCalendarMonths(today, -3).slice(0, 7), today.slice(0, 7)] }
    case 'YEAR':
      return { startDt: addCalendarMonths(today, -36).slice(0, 4), endDt: today.slice(0, 4) }
    default:
      return {}
  }
}

// 조회 기간 [start, end] + 비교 타입 → 비교 기간 [start, end] | null (yyyy-MM-dd)
// **달력 날짜 셈이다** — `new Date('YYYY-MM-DD')`(UTC 자정)를 로컬 칸으로 읽으면 음(−) 오프셋 브라우저에서 하루 이르다(R-L 리뷰 L-3).
// 월/년 이동은 말일을 넘치지 않는다(3/31 의 전월은 2/28, 윤년 2/29 의 전년은 2/28) — 셈은 `kstDate` 한 곳이다.
// 대시보드·통합 매출이 이 함수 하나를 쓴다(사본 없음).
export function computeCompareRange(range, compareType, customCompareRange) {
  if (!range || !range[0] || !range[1]) return null

  const [start, end] = range
  let compare

  switch (compareType) {
    case 'PREV_YEAR':
      compare = [addCalendarMonths(start, -12), addCalendarMonths(end, -12)]
      break
    case 'PREV_MONTH':
      compare = [addCalendarMonths(start, -1), addCalendarMonths(end, -1)]
      break
    case 'PREV_PERIOD': {
      const compareEnd = addCalendarDays(start, -1)
      compare = [addCalendarDays(compareEnd, -calendarDaysBetween(start, end)), compareEnd]
      break
    }
    case 'CUSTOM':
      return customCompareRange && customCompareRange[0] && customCompareRange[1] ? customCompareRange : null
    default:
      return null
  }

  return compare[0] && compare[1] ? compare : null
}

// 증감률 표시 문자열 (+12.3% / -4.5% / 0%)
// 분모는 Math.abs(compare) — 비교 기준이 음수(정산예상이 고할인/광고비로 음수 가능)일 때도
// 증감률 부호가 증감 방향(current - compare)과 일치하도록 한다. (그렇지 않으면 diffClass/diffIcon
// 의 대소 비교와 어긋나 '녹색 ▲ -300%' 같은 자기모순 표시가 발생한다.)
export function diffPercent(current, compare) {
  if (!compare) {
    // compare 가 0/null/undefined/NaN: 증감률 정의 불가 → 방향만 표기
    if (current > 0) return '+100%'
    if (current < 0) return '-100%'
    return '0%'
  }
  const diff = ((current - compare) / Math.abs(compare)) * 100
  const sign = diff >= 0 ? '+' : ''
  return `${sign}${diff.toFixed(1)}%`
}

// 증감 방향 CSS 클래스 (positive/negative/neutral)
export function diffClass(current, compare) {
  if (current > compare) return 'positive'
  if (current < compare) return 'negative'
  return 'neutral'
}

// 증감 방향 아이콘
export function diffIcon(current, compare) {
  if (current > compare) return 'el-icon-top'
  if (current < compare) return 'el-icon-bottom'
  return 'el-icon-minus'
}
